import type { ReactNode } from "react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../lib/authContext";

/**
 * Top-level gate: if there's no signed-in user once auth has resolved,
 * kick off the Okta redirect immediately. Until that happens (and on cold
 * page loads while we're still checking localStorage / refreshing the
 * token), show a minimal "Signing in…" splash so the user doesn't see
 * a flash of unauthenticated UI.
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { user, isLoading, signIn } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.expired) {
      // Pass the current path so the callback can return us here.
      signIn(location.pathname + location.search);
    }
  }, [isLoading, user, signIn, location.pathname, location.search]);

  if (isLoading || !user || user.expired) {
    return <AuthSplash />;
  }
  return <>{children}</>;
}

function AuthSplash() {
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-0)",
        color: "var(--text-mid)",
        fontFamily: "var(--mono)",
        fontSize: 13,
        letterSpacing: "0.04em",
      }}
    >
      <span>Signing in via Okta…</span>
    </div>
  );
}
